import type { ButtonHTMLAttributes } from 'react';
import { cx } from '../lib/cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-md border font-medium whitespace-nowrap ' +
  'transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const VARIANTS: Readonly<Record<ButtonVariant, string>> = {
  primary: 'border-accent bg-accent text-white hover:bg-accent-strong hover:border-accent-strong',
  secondary: 'border-line-stronger bg-base text-ink hover:bg-surface-2',
  ghost: 'border-transparent bg-transparent text-muted hover:bg-surface-2 hover:text-ink',
  danger: 'border-danger-border bg-danger-tint text-danger hover:bg-danger hover:text-white',
};

const SIZES: Readonly<Record<ButtonSize, string>> = {
  sm: 'h-8 px-3 fs-13',
  md: 'h-9 px-4 text-sm',
};

export interface ButtonStyleOptions {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly className?: string;
}

/**
 * Exported separately so an `<a>` can look like a button without being one.
 * The Google sign-in control is a real link (it leaves the SPA entirely), and
 * wrapping a cross-origin navigation in a <button onClick> would break
 * middle-click, "open in new tab", and the accessibility tree.
 */
export function buttonClassName({
  variant = 'secondary',
  size = 'md',
  className,
}: ButtonStyleOptions = {}): string {
  return cx(BASE, VARIANTS[variant], SIZES[size], className);
}

// The style options must win over the raw HTML attributes: both declare
// `className`, and TypeScript refuses to extend two interfaces that declare the
// same property differently (ButtonStyleOptions marks it readonly). Omitting the
// overlap resolves it without loosening either type.
export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonStyleOptions>, ButtonStyleOptions {}

export function Button({ variant, size, className, type = 'button', ...rest }: ButtonProps) {
  // Default to type="button": an untyped <button> inside a <form> submits it,
  // which turns every toolbar icon into an accidental save.
  return <button type={type} className={buttonClassName({ variant, size, className })} {...rest} />;
}
